import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { conhecimentoArticles } from "@/data/conhecimentoData";
import Footer from "@/components/landing/Footer";

export default function ConhecimentoHubPage() {
  useEffect(() => {
    document.title = "Conhecimento | KpAction";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Conteúdo técnico sobre dinâmica operacional, comportamento sistêmico da produção industrial, variabilidade, capacidade e regimes de operação."
      );
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Conhecimento - Inteligência de Dinâmica Operacional",
      "description":
        "Hub de conteúdo técnico sobre dinâmica operacional em ambientes industriais.",
      "url": "https://www.kpaction.com.br/conhecimento",
      "publisher": {
        "@type": "Organization",
        "@id": "https://www.kpaction.com.br/#organization",
        "name": "KpAction",
      },
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1115]" data-testid="conhecimento-hub-page">
      {/* Header */}
      <header className="border-b border-[#2A2F38] bg-[#0F1115]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#E5E7EB] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Voltar para Home</span>
          </a>
          <a href="/" className="font-heading text-xl font-bold">
            <span className="text-[#F59E0B]">Kp</span>
            <span className="text-[#E5E7EB]">Action</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-12" aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-[#6B7280]">
              <li>
                <a href="/" className="hover:text-[#F59E0B] transition-colors">
                  Home
                </a>
              </li>
              <li><span className="text-[#2A2F38]">/</span></li>
              <li className="text-[#9CA3AF]">Conhecimento</li>
            </ol>
          </nav>

          {/* Page Title */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
              Conhecimento
            </span>

            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#E5E7EB] mb-6">
              Inteligência de <span className="text-[#F59E0B]">Dinâmica Operacional</span>
            </h1>

            <p className="text-base md:text-lg text-[#9CA3AF] max-w-3xl mx-auto mb-8">
              Compreendendo o comportamento sistêmico de operações industriais.
            </p>
          </div>

          {/* Introductory Text */}
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8 md:p-10 mb-16">
            <div className="space-y-4 text-[#9CA3AF] leading-relaxed">
              <p>
                Sistemas produtivos são estruturas dinâmicas. Dentro de cada operação industrial, múltiplas variáveis interagem continuamente: capacidade, demanda, backlog, variabilidade e complexidade.
              </p>
              <p>
                Compreender essa dinâmica é essencial para a gestão moderna de operações. Em vez de observar indicadores isolados, a análise do comportamento sistêmico permite identificar padrões, tendências e estados operacionais que nem sempre são visíveis em relatórios tradicionais.
              </p>
              <p>
                Os artigos abaixo exploram conceitos fundamentais relacionados à dinâmica de sistemas produtivos industriais.
              </p>
            </div>
          </div>

          {/* Articles Grid */}
          <div className="space-y-6">
            {conhecimentoArticles.map((article, index) => (
              <Link
                key={article.slug}
                to={`/conhecimento/${article.slug}`}
                className="block bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 md:p-8 hover:border-[#F59E0B]/50 transition-all group"
                data-testid={`article-card-${index}`}
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#0E3A46] rounded-sm group-hover:bg-[#F59E0B]/10 transition-colors">
                    <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-[#F59E0B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-[#E5E7EB] mb-2 group-hover:text-[#F59E0B] transition-colors">
                      {article.titleDisplay}
                    </h2>
                    <p className="text-sm md:text-base text-[#9CA3AF] leading-relaxed mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-[#F59E0B]">
                      <span>Ler artigo</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
